import { useEffect, useState } from 'react';

import { type PlacementProps } from '@atlassian/post-office-placement-remote';

type UsePostOfficePlacementResult = {
	PostOfficePlacement: React.ComponentType<PlacementProps> | null;
	isMounted: boolean;
};

// Local type for test-only mock — avoids mutating the ambient Window interface
type WindowWithPostOfficePlacementMock = typeof globalThis & {
	__mockPostOfficePlacement__?: React.ComponentType<PlacementProps>;
};

/**
 * This hook dynamically imports the Placement component from the @atlassian/post-office-placement-remote package.
 * The dynamic import is used to avoid SSR altogether, as the Placement was clashing with SSR.
 * Supports test mocking via window.__mockPostOfficePlacement__
 */

export const usePostOfficePlacement = (): UsePostOfficePlacementResult => {
	const [PostOfficePlacement, setPostOfficePlacement] =
		useState<React.ComponentType<PlacementProps> | null>(null);
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			let isComponentMounted = true;

			// Check for mock in test environments (set by VR test)
			const mockModule = (window as WindowWithPostOfficePlacementMock).__mockPostOfficePlacement__;
			if (mockModule) {
				setPostOfficePlacement(() => mockModule as React.ComponentType<PlacementProps>);
				setIsMounted(true);
				return;
			}

			import('@atlassian/post-office-placement-remote')
				.then((module) => {
					if (isComponentMounted) {
						setPostOfficePlacement(() => module.Placement);
						setIsMounted(true);
					}
				})
				.catch(() => {
					if (isComponentMounted) {
						setIsMounted(true);
					}
				});

			return () => {
				isComponentMounted = false;
			};
		}
	}, []);

	return { PostOfficePlacement, isMounted };
};
